import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Zap, Truck, ShieldCheck, RefreshCw, Plus, Minus } from "lucide-react";
import { shopApi } from "@/shop/shopApi";
import { Reveal, MaskLines, easeOutExpo } from "@/shop/motion";
import ProductCard from "@/shop/ProductCard";
import { ShopEmpty, ProductSkeleton } from "@/shop/ShopStates";

const MARQUEE = ["FREE SHIPPING OVER $75", "ENGINEERED FOR THE SIGNAL", "SECURE SHOPIFY CHECKOUT", "NEW DROPS WEEKLY", "BUILT TO LAST"];

const WHY = [
  { icon: Zap, title: "Engineered, not assembled", body: "Every product is spec'd for performance and built to a standard, not a price point." },
  { icon: Truck, title: "Fast, tracked shipping", body: "Orders ship within 48 hours with end-to-end tracking on every parcel." },
  { icon: ShieldCheck, title: "Secure by default", body: "Checkout runs on Shopify's PCI-compliant infrastructure. Your data stays yours." },
  { icon: RefreshCw, title: "30-day easy returns", body: "If it isn't right, send it back within 30 days. No drama, no restocking fees." },
];

const REVIEWS = [
  { q: "The build quality is unreal. Feels like it costs three times the price.", a: "Marcus T.", loc: "Verified Buyer" },
  { q: "Fast shipping, flawless packaging, and the product just works. My new go-to.", a: "Priya S.", loc: "Verified Buyer" },
  { q: "Signal House gets the details right. This is how ecommerce should feel.", a: "Devon R.", loc: "Verified Buyer" },
];

const FAQS = [
  { q: "How long does shipping take?", a: "Most orders ship within 48 hours and arrive in 3–5 business days within the US. International times vary by destination and are shown at checkout." },
  { q: "What is your return policy?", a: "We offer 30-day hassle-free returns on unused items in original packaging. Start a return from your order confirmation email or the Track Order page." },
  { q: "Is checkout secure?", a: "Yes. Checkout is handled by Shopify's PCI-DSS Level 1 compliant platform. We never see or store your card details." },
  { q: "Can I track my order?", a: "Absolutely. You'll receive a tracking link by email as soon as your order ships, and you can look it up anytime on the Track Order page." },
];

export default function ShopHome() {
  const [featured, setFeatured] = useState({ loading: true, configured: true, collections: [] });
  const [best, setBest] = useState({ loading: true, items: [] });
  const [fresh, setFresh] = useState({ loading: true, items: [] });
  const [faqOpen, setFaqOpen] = useState(0);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    shopApi.collections().then((d) => setFeatured({ loading: false, configured: d.configured, collections: d.collections })).catch(() => setFeatured({ loading: false, configured: false, collections: [] }));
    shopApi.products({ sort: "best-selling", first: 8 }).then((d) => setBest({ loading: false, items: d.products || [] })).catch(() => setBest({ loading: false, items: [] }));
    shopApi.products({ sort: "newest", first: 4 }).then((d) => setFresh({ loading: false, items: d.products || [] })).catch(() => setFresh({ loading: false, items: [] }));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try { await shopApi.config(); } catch (_) { /* noop */ }
    try {
      const { api } = await import("@/lib/api");
      await api.post("/newsletter", { email });
    } catch (_) { /* noop */ }
    setSubscribed(true);
    setEmail("");
  };

  return (
    <div data-testid="shop-home">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center overflow-hidden">
        <div className="absolute inset-0 sh-hero-grid" />
        <div className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] sh-glow" />
        <motion.div style={{ y: heroY, opacity: heroOpacity }} className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 w-full pt-24">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: easeOutExpo }}
            className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-white/70 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-sh-blue animate-pulse" /> Now shipping worldwide
          </motion.div>

          <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[0.92] text-[15vw] sm:text-[12vw] lg:text-[8.5vw]" data-testid="hero-headline">
            <MaskLines lines={["Engineered", "for the signal."]} delay={0.15} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.7 }}
            className="mt-8 max-w-xl text-lg text-white/60 leading-relaxed"
          >
            Premium gear and goods, obsessively built and cut through the noise. Welcome to Signal House.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: easeOutExpo, delay: 0.85 }}
            className="mt-11 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/shop/all" className="group inline-flex items-center justify-center gap-2.5 h-14 px-9 rounded-full bg-white text-black font-medium hover:bg-sh-blue hover:text-white transition-colors duration-300" data-testid="hero-shop-cta">
              Shop the collection <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/shop/collections" className="inline-flex items-center justify-center h-14 px-9 rounded-full border border-white/25 text-white hover:bg-white/5 transition-colors duration-300">
              Browse collections
            </Link>
          </motion.div>
        </motion.div>

        <div className="absolute bottom-0 inset-x-0 border-t border-white/10 bg-black/40 backdrop-blur-sm py-4 overflow-hidden z-10">
          <div className="sh-marquee flex gap-12 whitespace-nowrap will-change-transform">
            {[...MARQUEE, ...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="text-[13px] tracking-[0.25em] uppercase text-white/45 flex items-center gap-12">
                {m} <span className="text-sh-blue">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED COLLECTIONS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <Reveal className="flex items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">01 — Featured</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.03em]">Shop by collection</h2>
          </div>
          <Link to="/shop/collections" className="hidden md:inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">All collections <ArrowUpRight className="w-4 h-4" /></Link>
        </Reveal>

        {featured.loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">{[0,1,2].map((i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-white/5 animate-pulse" />)}</div>
        ) : !featured.configured || featured.collections.length === 0 ? (
          <ShopEmpty title="Collections launching soon" note="Your Shopify collections will render here automatically once connected." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="featured-collections">
            {featured.collections.slice(0, 6).map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 0.08}>
                <Link to={`/shop/collections/${c.handle}`} className="group relative block aspect-[3/4] rounded-2xl overflow-hidden bg-[#161619]" data-testid={`collection-card-${c.handle}`}>
                  {c.image && <img src={c.image} alt={c.title} loading="lazy" className="w-full h-full object-cover opacity-80 transition-all duration-[900ms] group-hover:scale-105 group-hover:opacity-100" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between">
                    <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                    <ArrowUpRight className="w-6 h-6 text-white/70 group-hover:text-sh-blue group-hover:rotate-45 transition-all duration-500" />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* BEST SELLERS */}
      <ProductRow eyebrow="02 — Best Sellers" title="What everyone is buying" state={best} to="/shop/all" />

      {/* WHY SHOP */}
      <section className="border-y border-white/10 bg-[#0a0a0c]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <Reveal><p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">03 — Why Signal House</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.03em] max-w-3xl">Details you feel. Standards you trust.</h2></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.08}>
                <div className="h-full border border-white/10 rounded-2xl p-7 hover:border-sh-blue/50 transition-colors duration-500" data-testid={`why-card-${i}`}>
                  <w.icon className="w-7 h-7 text-sh-blue" strokeWidth={1.4} />
                  <h3 className="font-display text-xl font-semibold mt-8">{w.title}</h3>
                  <p className="text-sm text-white/55 mt-3 leading-relaxed">{w.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <ProductRow eyebrow="04 — New Arrivals" title="Just dropped" state={fresh} to="/shop/all?sort=newest" cols={4} />

      {/* REVIEWS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
        <Reveal><p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">05 — Reviews</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.03em] max-w-3xl">Loved by thousands.</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08}>
              <figure className="h-full border border-white/10 rounded-2xl p-8 bg-[#0e0e10]" data-testid={`review-${i}`}>
                <div className="flex gap-1 text-sh-blue mb-5">{"★★★★★".split("").map((s, k) => <span key={k}>{s}</span>)}</div>
                <blockquote className="font-display text-xl leading-snug">{`"${r.q}"`}</blockquote>
                <figcaption className="mt-6 text-sm text-white/55">{r.a} · <span className="text-white/40">{r.loc}</span></figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-5 md:px-10 py-24 md:py-32">
          <Reveal><p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4 text-center">06 — FAQ</p></Reveal>
          <Reveal delay={0.05}><h2 className="font-display text-4xl md:text-5xl font-bold tracking-[-0.03em] text-center mb-14">Questions, answered.</h2></Reveal>
          <div className="divide-y divide-white/10 border-y border-white/10">
            {FAQS.map((f, i) => (
              <div key={i} data-testid={`faq-${i}`}>
                <button onClick={() => setFaqOpen(faqOpen === i ? -1 : i)} className="w-full flex items-center justify-between gap-4 py-6 text-left">
                  <span className="font-display text-lg font-medium">{f.q}</span>
                  {faqOpen === i ? <Minus className="w-5 h-5 text-sh-blue shrink-0" /> : <Plus className="w-5 h-5 text-white/50 shrink-0" />}
                </button>
                <motion.div initial={false} animate={{ height: faqOpen === i ? "auto" : 0, opacity: faqOpen === i ? 1 : 0 }} transition={{ duration: 0.4, ease: easeOutExpo }} className="overflow-hidden">
                  <p className="pb-6 text-white/60 leading-relaxed">{f.a}</p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="border-t border-white/10 bg-[#0a0a0c]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
          <div className="relative rounded-3xl border border-white/10 overflow-hidden p-10 md:p-16">
            <div className="absolute -right-20 -top-20 w-80 h-80 sh-glow" />
            <div className="relative max-w-xl">
              <h2 className="font-display text-4xl md:text-5xl font-bold tracking-[-0.03em]">Get on the list.</h2>
              <p className="text-white/60 mt-4">{`Early access to drops, subscriber-only pricing, and zero spam.`}</p>
              {subscribed ? (
                <p className="mt-8 text-sh-blue font-medium" data-testid="newsletter-success">{`You're in. Watch your inbox. ✦`}</p>
              ) : (
                <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="shop-newsletter-form">
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="flex-1 h-14 px-6 rounded-full bg-white/5 border border-white/15 focus:border-sh-blue outline-none text-white placeholder:text-white/30" data-testid="shop-newsletter-email" />
                  <button type="submit" className="h-14 px-8 rounded-full bg-sh-blue text-white font-medium hover:bg-[#3b9bff] transition-colors" data-testid="shop-newsletter-submit">Subscribe</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ProductRow({ eyebrow, title, state, to, cols = 4 }) {
  const gridCls = cols === 4 ? "grid grid-cols-2 lg:grid-cols-4 gap-5" : "grid grid-cols-2 lg:grid-cols-3 gap-5";
  const skeletonCount = cols;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32">
      <Reveal className="flex items-end justify-between gap-6 mb-12">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-sh-blue mb-4">{eyebrow}</p>
          <h2 className="font-display text-4xl md:text-6xl font-bold tracking-[-0.03em]">{title}</h2>
        </div>
        <Link to={to} className="hidden md:inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">View all <ArrowUpRight className="w-4 h-4" /></Link>
      </Reveal>
      {state.loading ? (
        <div className={gridCls}>{Array.from({length: skeletonCount}).map((_, i) => <ProductSkeleton key={i} />)}</div>
      ) : state.items.length === 0 ? (
        <ShopEmpty />
      ) : (
        <div className={gridCls}>
          {state.items.slice(0, cols === 4 ? 8 : cols).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </section>
  );
}
